import { addDays, format } from "date-fns";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { APIs } from "../../apis";
import { OrderItemPlanningTab, type OrderItem } from "../../apis/order";
import {
  SortOrder,
  type TableColumn,
  type TableSorter,
} from "../../components/Table";
import { useApi, useFilters, useNavigation } from "../../hooks";
import { Constants, Tools } from "../../utils";
import { Tags } from "../../libs";
import { TagsEvent, TagsContentType, TagsMode } from "../../libs/tags";
import { getManageBookingsColumns } from "./columns";

const DEFAULT_PAGE_SIZE = 10;
const ORDER_BY_OPTIONS = [
  "",
  ...getManageBookingsColumns({ t: (key) => key }).flatMap((c) =>
    c.sorter && c.key ? [String(c.key), `-${c.key}`] : [],
  ),
];

export function useViewModel() {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { fetch, data, loading } =
    useApi<Awaited<ReturnType<typeof APIs.order.orderItems>>>();

  const defaultDateRange = React.useMemo(() => {
    const today = new Date();
    return {
      from: format(today, "yyyy-MM-dd"),
      to: format(addDays(today, 30), "yyyy-MM-dd"),
    };
  }, []);

  const skeletonOnNextLoad = React.useRef(true);

  const { filters, setFilter, setSearchParams, isValid } = useFilters({
    defaultValues: {
      searchString: "",
      startDate: defaultDateRange.from,
      endDate: defaultDateRange.to,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: "-serviceDateTime",
    },
    validate: (params) => ({
      orderBy: !Tools.isOneOf(params.orderBy, ORDER_BY_OPTIONS),
    }),
    modifyFilter: (name, value: any) => {
      skeletonOnNextLoad.current = name !== "orderBy";

      if (name === "searchString") {
        Tags.event(TagsEvent.SearchList, {
          screen: Constants.RoutePath.ManageBookings,
        });
      } else if (name === "orderBy") {
        Tags.event(TagsEvent.SortList, {
          screen: Constants.RoutePath.ManageBookings,
          sort_by: String(value),
        });
      } else {
        Tags.event(TagsEvent.ApplyFilter, {
          screen: Constants.RoutePath.ManageBookings,
          filter_type: name,
          value:
            name === "dateRange"
              ? Tools.formatDateRange(value?.from, value?.to)
              : String(value),
        });
      }

      return {
        ...(name === "dateRange" && value
          ? {
              startDate: value.from,
              endDate: value.to,
            }
          : { [name]: value }),
        page: 1,
      };
    },
  });

  const { results: dataSource = [], count: totalItems = 0 } =
    data?.orders ?? {};

  const showSkeleton = loading && skeletonOnNextLoad.current;
  const showGlobalEmpty = !loading && !totalItems && filters.page === 1;

  const openBookingDetail = React.useCallback(
    (item: OrderItem) => {
      Tags.event(TagsEvent.SelectContent, {
        content_type: TagsContentType.BookingDetail,
        item_id: String(item.orderId ?? item.order?.id ?? ""),
      });
      navigate(
        `${Constants.RoutePath.ManageBookings}/${item.orderId ?? item.order?.id}`,
      );
    },
    [navigate],
  );

  const columns = React.useMemo(() => getManageBookingsColumns({ t }), [t]);

  const tableSorter = React.useMemo((): TableSorter | undefined => {
    if (!filters.orderBy) return undefined;
    const order = filters.orderBy.startsWith("-")
      ? SortOrder.descend
      : SortOrder.ascend;
    return {
      field: filters.orderBy.slice(order === SortOrder.descend ? 1 : 0),
      order,
    };
  }, [filters.orderBy]);

  const getData = React.useCallback(async () => {
    if (!isValid) return;
    try {
      await fetch(() =>
        APIs.order.orderItems({
          page: filters.page,
          pageSize: filters.pageSize,
          orderBy: filters.orderBy || undefined,
          planningTab: OrderItemPlanningTab.all,
          searchString: filters.searchString,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }),
      );
    } catch {
    } finally {
      skeletonOnNextLoad.current = false;
    }
  }, [
    fetch,
    filters.orderBy,
    filters.startDate,
    filters.endDate,
    filters.page,
    filters.pageSize,
    filters.searchString,
    isValid,
  ]);

  React.useEffect(() => {
    void getData();
  }, [getData]);

  const onTableChange = React.useCallback(
    ({ sorter }: { sorter?: TableSorter }) => {
      setFilter(
        "orderBy",
        sorter
          ? `${sorter.order === SortOrder.descend ? "-" : ""}${sorter.field}`
          : "",
      );
    },
    [setFilter],
  );

  const onPageChange = React.useCallback(
    (page: number) => {
      Tags.event(TagsEvent.PaginateList, {
        screen: Constants.RoutePath.ManageBookings,
        mode: TagsMode.Page,
        page,
      });
      setSearchParams({ page });
    },
    [setSearchParams],
  );

  const onPageSizeChange = React.useCallback(
    (pageSize: number) => {
      Tags.event(TagsEvent.PaginateList, {
        screen: Constants.RoutePath.ManageBookings,
        mode: TagsMode.PageSize,
        page_size: pageSize,
      });
      setSearchParams({ pageSize, page: 1 });
    },
    [setSearchParams],
  );

  return {
    t,
    columns: columns as TableColumn<OrderItem>[],
    tableSorter,
    dataSource,
    totalItems,
    loading,
    showSkeleton,
    showGlobalEmpty,
    page: filters.page,
    pageSize: filters.pageSize,
    searchString: filters.searchString,
    dateRange: {
      from: filters.startDate,
      to: filters.endDate,
    },
    setFilter,
    onPageChange,
    onPageSizeChange,
    onRowClick: openBookingDetail,
    onTableChange,
  };
}

export type ManageBookingsViewModel = ReturnType<typeof useViewModel>;
